import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useShallow } from 'zustand/shallow'
import type { Moment } from 'moment'
import { ActivityType } from '@/app/constants/activity'
import { AddActivityMode } from '@/app/constants/addActivityMode'
import { getActivityTypeNumber } from '@/modules/drivers/helpers/ActivityTypeResolver'
import type { LeaveSummary } from '@/modules/drivers/tabs/activities/utils/leaveSummary'
import type { DatePickerRangeProps } from '@/modules/drivers/types'
import { zustandPlugin } from '@/modules/shared/openreplay'

type ActivityPagesTypes =
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'yearly'
  | 'time'
  | 'cardData'
  | 'details'
  | 'compare'
  | 'chartDetails'

export type ActivityForm = {
  showDefaultData: true
  startDate: Moment | null
  endDate: Moment | null
  overrideType: AddActivityMode
  activityType: number
}

type Setter<T> = React.Dispatch<React.SetStateAction<T>>

type State = {
  type: ActivityPagesTypes
  showDefaultData: boolean
  showTmsData: boolean
  vehicleTachographMode: boolean
  showYearlyTrips: boolean
  hasVehicleSourceActivities: boolean
  hasTachographEvents: boolean
  leaveSummary: LeaveSummary | null
  data: ActivityForm
  refreshTrigger: number
  chartRange: DatePickerRangeProps | null
}

type Actions = {
  setType: Setter<ActivityPagesTypes>
  setShowDefaultData: Setter<boolean>
  setShowTmsData: Setter<boolean>
  setVehicleTachographMode: Setter<boolean>
  setShowYearlyTrips: Setter<boolean>
  setHasVehicleSourceActivities: Setter<boolean>
  setHasTachographEvents: Setter<boolean>
  setLeaveSummary: Setter<LeaveSummary | null>
  setData: Setter<ActivityForm>
  setChartRange: Setter<DatePickerRangeProps | null>
  triggerRefresh: () => void
}

const initialData: ActivityForm = {
  showDefaultData: true,
  startDate: null,
  endDate: null,
  overrideType: AddActivityMode.EACH_ACTIVITY,
  activityType: getActivityTypeNumber(ActivityType.AVAILABILITY),
}

const initialState: State = {
  type: 'daily',
  showDefaultData: true,
  showTmsData: false,
  vehicleTachographMode: false,
  showYearlyTrips: false,
  hasVehicleSourceActivities: false,
  hasTachographEvents: false,
  leaveSummary: null,
  data: initialData,
  refreshTrigger: 0,
  chartRange: null,
}

const makeSetter =
  <K extends keyof State>(
    set: (partial: Partial<State>, replace?: boolean, action?: string) => void,
    get: () => State,
    key: K,
    actionName: string,
  ): Setter<State[K]> =>
  (updater) => {
    const prev = get()[key]
    const next =
      typeof updater === 'function'
        ? (updater as (p: State[K]) => State[K])(prev)
        : updater
    set({ [key]: next } as Partial<State>, false, actionName)
  }

export const useDriverActivityStore = create<State & Actions>()(
  devtools(
    zustandPlugin(
      (set, get) => ({
        ...initialState,
        setType: makeSetter(set, get, 'type', 'setType'),
        setShowDefaultData: makeSetter(
          set,
          get,
          'showDefaultData',
          'setShowDefaultData',
        ),
        setShowTmsData: makeSetter(set, get, 'showTmsData', 'setShowTmsData'),
        setVehicleTachographMode: makeSetter(
          set,
          get,
          'vehicleTachographMode',
          'setVehicleTachographMode',
        ),
        setShowYearlyTrips: makeSetter(
          set,
          get,
          'showYearlyTrips',
          'setShowYearlyTrips',
        ),
        setHasVehicleSourceActivities: makeSetter(
          set,
          get,
          'hasVehicleSourceActivities',
          'setHasVehicleSourceActivities',
        ),
        setHasTachographEvents: makeSetter(
          set,
          get,
          'hasTachographEvents',
          'setHasTachographEvents',
        ),
        setLeaveSummary: makeSetter(
          set,
          get,
          'leaveSummary',
          'setLeaveSummary',
        ),
        setData: makeSetter(set, get, 'data', 'setData'),
        setChartRange: makeSetter(set, get, 'chartRange', 'setChartRange'),
        triggerRefresh: () =>
          set(
            (s) => ({ refreshTrigger: s.refreshTrigger + 1 }),
            false,
            'triggerRefresh',
          ),
      }),
      'driver-activity',
    ),
    { name: 'EasyTacho', store: 'driver-activity' },
  ),
)

export const useActivity = () =>
  useDriverActivityStore(
    useShallow((s) => ({
      type: s.type,
      setType: s.setType,
      showDefaultData: s.showDefaultData,
      setShowDefaultData: s.setShowDefaultData,
      showTmsData: s.showTmsData,
      setShowTmsData: s.setShowTmsData,
      vehicleTachographMode: s.vehicleTachographMode,
      setVehicleTachographMode: s.setVehicleTachographMode,
      showYearlyTrips: s.showYearlyTrips,
      setShowYearlyTrips: s.setShowYearlyTrips,
      hasVehicleSourceActivities: s.hasVehicleSourceActivities,
      setHasVehicleSourceActivities: s.setHasVehicleSourceActivities,
      hasTachographEvents: s.hasTachographEvents,
      setHasTachographEvents: s.setHasTachographEvents,
      leaveSummary: s.leaveSummary,
      setLeaveSummary: s.setLeaveSummary,
      data: s.data,
      setData: s.setData,
      chartRange: s.chartRange,
      setChartRange: s.setChartRange,
      refreshTrigger: s.refreshTrigger,
      triggerRefresh: s.triggerRefresh,
    })),
  )
